import {useCallback, useContext, useRef} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useHttpCalls} from '../../../api/useHttpCalls';
import {EagGridStoreContext} from '../../../store/EagGridStoreContext';
import {createLruMap} from '../../../utils/createLruMap';

const EMPTY_VALUE = decodeURI('%00');

type SuggestionOption = {value: string; label: string};

type UseSuggestionCacheConfig = {
  maxSize: number;
  suggestId: string;
  http: Pick<ReturnType<typeof useHttpCalls>, 'getSuggestionOptions' | 'getSuggestionLabels'>;
};

/**
 * Per-instance LRU cache for SuggestionFilter option lookups.
 *
 * The options cache is held in a `useRef` so it survives re-renders but is scoped
 * to a single SuggestionFilter instance (not shared across columns/filters). It is
 * backed by the shared {@link createLruMap} utility, which evicts the oldest entry
 * once `maxSize` is exceeded. Cache keys are `${suggestId}:${query}` so the same
 * instance can cache results for multiple queries.
 *
 * `fetchOptions` / `fetchLabels` have stable identity (they only re-create when
 * `http` or `suggestId` change), matching the contract AsyncMultiChoice expects.
 */
export function useSuggestionCache(config: UseSuggestionCacheConfig) {
  const {maxSize, suggestId, http} = config;

  const optionsCacheRef = useRef(createLruMap<string, Array<SuggestionOption>>(maxSize));

  // Non-strict store read: the SuggestionFilter always renders within a store provider in the
  // app, so its fetched options/labels are written through to the shared suggestionLabelsCache
  // (read by the active-filter bar). null in isolated unit tests — write-through simply no-ops.
  const store = useContext(EagGridStoreContext);

  const fetchOptions = useCallback(
    async (query: string) => {
      const cacheKey = `${suggestId}:${query}`;
      const cached = optionsCacheRef.current.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }

      const result = await http.getSuggestionOptions({
        suggestId,
        inputValue: isNilOrEmpty(query) ? EMPTY_VALUE : query,
        limit: 20,
      });
      const options = result ?? [];

      optionsCacheRef.current.set(cacheKey, options);
      store?.setSuggestionLabels(suggestId, options);
      return options;
    },
    [http, suggestId, store]
  );

  const fetchLabels = useCallback(
    async (values: Array<string>) => {
      const result = await http.getSuggestionLabels({
        suggestId,
        keys: values,
      });
      const labels = result ?? [];
      store?.setSuggestionLabels(suggestId, labels);
      return labels;
    },
    [http, suggestId, store]
  );

  return {fetchOptions, fetchLabels};
}
